// The ellipse that best follows an outline, read as a radius per angle around a centre — as the
// detour pipeline rounds a stone: fitted robustly, so that a bump or a dent does not drag it. The
// pipeline only rounds an outline already close to a circle; here it is fitted whatever the shape.

// A conic A·x² + B·xy + C·y² + D·x + E·y + F = 0, in coordinates shifted and scaled for the fit.
type Conic = {
  A: number;
  B: number;
  C: number;
  D: number;
  E: number;
  F: number;
};

// How far from the fitted ellipse a point may lie and still count, as in the pipeline: k robust
// deviations, or at least a small share of the radius; and the share of the outline that must stay.
const K = 2.5;
const TRIM_FRACTION = 0.02;
const MIN_KEPT = 0.55;
const ROUNDS = 12;

function solve(matrix: number[][], vector: number[]): number[] | null {
  // Gaussian elimination with partial pivoting, on a small square system.
  const n = vector.length;
  const m = matrix.map((row, i) => [...row, vector[i]]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++)
      if (Math.abs(m[row][col]) > Math.abs(m[pivot][col])) pivot = row;
    if (Math.abs(m[pivot][col]) < 1e-12) return null;
    [m[col], m[pivot]] = [m[pivot], m[col]];
    for (let row = col + 1; row < n; row++) {
      const f = m[row][col] / m[col][col];
      for (let k = col; k <= n; k++) m[row][k] -= f * m[col][k];
    }
  }
  const x = new Array(n).fill(0);
  for (let row = n - 1; row >= 0; row--) {
    let s = m[row][n];
    for (let k = row + 1; k < n; k++) s -= m[row][k] * x[k];
    x[row] = s / m[row][row];
  }
  return x;
}

function leastSquares(rows: number[][], targets: number[]): number[] | null {
  const n = rows[0].length;
  const ata = [...Array(n)].map(() => new Array(n).fill(0));
  const atb = new Array(n).fill(0);
  rows.forEach((row, k) => {
    for (let i = 0; i < n; i++) {
      atb[i] += row[i] * targets[k];
      for (let j = 0; j < n; j++) ata[i][j] += row[i] * row[j];
    }
  });
  return solve(ata, atb);
}

// The best ellipse through the points (a conic with A + C = 1, a constraint no rotation changes),
// or null when the best conic is not an ellipse.
function fitEllipse(points: number[][]): Conic | null {
  if (points.length < 5) return null;
  const s = leastSquares(
    points.map(([x, y]) => [x * x - y * y, x * y, x, y, 1]),
    points.map(([, y]) => -y * y),
  );
  if (!s) return null;
  const [A, B, D, E, F] = s;
  const C = 1 - A;
  return B * B - 4 * A * C < 0 ? { A, B, C, D, E, F } : null;
}

// The best circle through the points, as a conic: the fallback when no ellipse fits.
function fitCircle(points: number[][]): Conic | null {
  const s = leastSquares(
    points.map(([x, y]) => [2 * x, 2 * y, 1]),
    points.map(([x, y]) => x * x + y * y),
  );
  if (!s) return null;
  const [a, b, c] = s;
  return { A: 1, B: 0, C: 1, D: -2 * a, E: -2 * b, F: -c };
}

// How far the conic's edge is from (cx, cy) along the ray of the given angle, or null when the
// ray does not cross it (the centre outside the ellipse).
function rayRadius(conic: Conic, cx: number, cy: number, angle: number) {
  let { A, B, C, D, E, F } = conic;
  if (A < 0) [A, B, C, D, E, F] = [-A, -B, -C, -D, -E, -F];
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const a = A * dx * dx + B * dx * dy + C * dy * dy;
  const b =
    2 * A * cx * dx +
    B * (cx * dy + cy * dx) +
    2 * C * cy * dy +
    D * dx +
    E * dy;
  const c = A * cx * cx + B * cx * cy + C * cy * cy + D * cx + E * cy + F;
  const discriminant = b * b - 4 * a * c;
  if (a <= 0 || discriminant < 0) return null;
  const t = (-b + Math.sqrt(discriminant)) / (2 * a);
  return t > 0 ? t : null;
}

const median = (values: number[]) => {
  const sorted = [...values].sort((x, y) => x - y);
  const middle = sorted.length >> 1;
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

// The radii, one per angle of `r`, of the ellipse that best follows the outline `r` around
// (cx, cy). Always an answer for an outline that encloses any area: the best circle when no
// ellipse fits, the outline itself when not even a circle does.
export function fittedEllipseRadii(
  cx: number,
  cy: number,
  r: number[],
): number[] {
  const bins = r.length;
  const angles = r.map((_, i) => (i / bins) * 2 * Math.PI);
  // Fitted around the centre and in units of the outline's size, so the squares stay well scaled.
  const scale = Math.max(...r, 1);
  const points = r.map((radius, i) => [
    (radius * Math.cos(angles[i])) / scale,
    (radius * Math.sin(angles[i])) / scale,
  ]);
  const radiiOf = (conic: Conic | null) => {
    if (!conic) return null;
    const radii = angles.map((angle) => rayRadius(conic, 0, 0, angle));
    return radii.every((t) => t !== null)
      ? radii.map((t) => (t as number) * scale)
      : null;
  };

  const circle = radiiOf(fitCircle(points));
  const circleRadius = circle ? median(circle) : median(r);

  // Fit, drop what lies too far from the fit, and fit again, while most of the outline stays.
  let kept = r.map(() => true);
  let best = radiiOf(fitEllipse(points));
  for (let round = 0; best && round < ROUNDS; round++) {
    const fitted: number[] = best;
    const keptBefore = kept;
    const residual = r.map((radius, i) => radius - fitted[i]);
    const inKept = residual.filter((_, i) => keptBefore[i]);
    const centre = median(inKept);
    const deviation =
      1.4826 * median(inKept.map((v) => Math.abs(v - centre))) + 1e-6;
    const reach = Math.max(K * deviation, TRIM_FRACTION * circleRadius);
    const aligned = residual.map((v) => Math.abs(v) <= reach);
    const count = aligned.filter(Boolean).length;
    if (count < MIN_KEPT * bins || aligned.every((a, i) => a === keptBefore[i]))
      break;
    kept = aligned;
    const refit = radiiOf(fitEllipse(points.filter((_, i) => aligned[i])));
    if (!refit) break;
    best = refit;
  }
  return best ?? circle ?? r;
}
